import { Node } from '@tiptap/pm/model';
import { TBlockSaveInfo } from '../types';
import { BLOCK_NODE_NAME } from '../tiptap/nodes/block/constants';
// The leaf constant, not `ETiptapSelectorKey`: that enum lives in a React component module, and
// importing it here pulls the app's whole component graph into anything that walks a document -
// including a unit test, which then cannot load at all.
import { HEADING_NODE_NAME } from '../tiptap/nodes/heading/headingNumbering';
import { INLINE_BLOCK_NODE_NAME } from '../tiptap/nodes/inlineBlock/constants';
import { isRegistrationSettled } from './isRegistrationSettled.helper';

/**
 * Doc-based, not editor-based: the caller decides WHICH document this is. The save path passes the
 * whole document, which for a large one is more than the editor holds - and save REPLACES the stored
 * lists, so collecting from one part would deregister everything outside it.
 */
export const collectNodesOfType = (doc: Node, type: string) => {
  const nodes: Node[] = [];

  doc.descendants((node) => {
    if (node.type.name === type) {
      nodes.push(node);
    }
  });

  return nodes;
};

/**
 * `registeredIds` is the authoritative answer to "has the backend got this block", from
 * `doc.blocksData`. `loadedId` is only a node view's record that it saw its own registration settle,
 * and a node outside the part the editor holds has no view - so filtering on `loadedId` alone drops
 * every block in the rest of the document, and save REPLACES the stored list.
 */
export const collectBlocksSaveInfo = (doc: Node, registeredIds?: ReadonlySet<string>): TBlockSaveInfo[] => {
  const blockSaveInfo: TBlockSaveInfo[] = [];
  let headingId: string | undefined = undefined;

  doc.descendants((node) => {
    if (node.type.name === HEADING_NODE_NAME) headingId = node.attrs.id;
    // only save loaded blocks:
    else if (
      [BLOCK_NODE_NAME, INLINE_BLOCK_NODE_NAME].includes(node.type.name) &&
      (isRegistrationSettled(node.attrs) || registeredIds?.has(node.attrs.id))
    ) {
      blockSaveInfo.push({
        id: node.attrs.id,
        headingId,
      });
    }
  });

  return blockSaveInfo;
};
